import React, {
  useEffect,
  forwardRef,
  useCallback,
  useContext,
  useRef,
  useState,
} from "react";
import {BryntumSchedulerPro} from "@bryntum/schedulerpro-react";
import { DateHelper } from "@bryntum/schedulerpro";
import {SCHEDULER_CONFIG} from "../../other/constants/SchedulerConfig.jsx";
import BryntumContext from "../context/bryntumContext.jsx";
import {EDITOR_KEYS} from "../../other/constants/EditorKeys.jsx";
import { canOpenEditor, debounce } from "../../other/utils/otherUtils.jsx";
import SwivuSchedulerMenuPlugin from "../../bryntum-lib/plugins/SwivuSchedulerMenuPlugin.jsx";
import SwivuSchedulerEventMenuPlugin from "../../bryntum-lib/plugins/SwivuSchedulerEventMenuPlugin.jsx";
import {processCellMenuItems} from "../../other/utils/gridCellMenuUtils.jsx";
import {GRANTS} from "../../other/constants/PrivilegeTypes.jsx";
import {WIDGET_KEYS} from "../../other/constants/WidgetKeys.jsx";
import { STORE_KEYS } from "../../other/constants/StoreKeys.jsx";
import { getOriginalRecord } from "../../other/utils/utils.jsx";
import { openAdjustPointsEditorWithGuard } from "../../other/utils/incentiveUtils.jsx";
import useViews from "../hooks/useViews.jsx";
import { ENTITY_TYPES } from "@/other/constants/OtherKeys.jsx";
import { WidgetParamKeys } from "@/types/entity";
import { CopyToBoardDialog } from "@/react/components/CopyToBoardDialog";

const BryntumSchedulerWrapper = forwardRef(({ onTaskFind, ...props }, ref) => {
  const viewManagerState  = useViews();
  const schedulerRef = useRef(null);
  const context = useContext(BryntumContext);
  const {editorHandler, project, user, currentViewRef, entityManager} = context;
  const [bulkCopyRecords, setBulkCopyRecords] = useState(null);

  useEffect(() => {
    if (schedulerRef.current) {
      schedulerRef.current.instance.features.cellMenu.processItems = processCellMenuItems({
        entityName: "Resource",
        editorHandler,
        grid: schedulerRef.current.instance,
        user,
        promptOnRemove: true,
        project,
        tagStoreKey: STORE_KEYS.resourceTagValuesStore,
        onBulkCopyTo: (selectedRecords) => setBulkCopyRecords(selectedRecords),
        onEditEntity(record) {
          const originalRecord = getOriginalRecord(record);
          if (user.can(GRANTS.grant_four)) {
            entityManager.openEntityManager({
              entityType: ENTITY_TYPES.RESOURCE,
              entityId: originalRecord.id
            });
          }
        }
      });
    }
  }, [editorHandler, user]);

  /* Keeping these clean up function in effects with other dependencies causes the ref to reset so any editors using it faces causes null, therefore keeping it here */
  useEffect(() => {
    return () => {
      currentViewRef.current.swivuLastViewportCenterDate = null;
    }
  }, [currentViewRef]);

  useEffect(() => {
    return () => {
      // Bug:
      // When timespans are highlighted and scheduler view state is switch (e.g: Default view to Custom view).
      // It causes a internal crash in scheduler therefore un-highlighting records when switching state
      if (schedulerRef.current) {
        schedulerRef.current.instance.features.timeSpanHighlight.unhighlightTimeSpans();
      }
    }
  }, [viewManagerState]);

  const onCreateNewTask = useCallback(({date, resourceRecord}) => {
    const timeAxis = schedulerRef.current.instance.timeAxis;
    const { startDate, endDate } = timeAxis.getAt(Math.floor(timeAxis.getTickFromDate(date)));
    const newRecord = project.extraTaskStore.createRecord();
    const resource = getOriginalRecord(resourceRecord);
    editorHandler.openEditor(EDITOR_KEYS.taskEditor, newRecord, {
      task: newRecord,
      onAfterRecordAdd: (taskRecord) => {
        const [newEvent] = project.eventStore.add({
          startDate,
          endDate,
          taskId: taskRecord.id
        });
        project.assignmentStore.add({
          resource,
          event: newEvent
        });
      }
    });
  }, [editorHandler, project]);

  const onCreateNewTaskEventByDragCreate = useCallback(({context, resourceRecord}) => {
    context.async = true;
    // by default, bryntum tries to make a sync call for record creation but we should prevent it as bryntum record creation doesn't include task record
    context.finalize(false);
    const {startDate, endDate} = context;
    const resource = getOriginalRecord(resourceRecord);
    const newRecord = project.extraTaskStore.createRecord();
    editorHandler.openEditor(EDITOR_KEYS.taskEditor, newRecord, {
      task: newRecord,
      onAfterRecordAdd: (taskRecord) => {
        const [newEvent] = project.eventStore.add({
          startDate,
          endDate,
          taskId: taskRecord.id
        });
        project.assignmentStore.add({
          resource,
          event: newEvent
        });
      }
    });
  }, [editorHandler, project]);

  const onCreateNewTaskReplaceEvent = useCallback(({date, resourceRecord, eventRecord}) => {
    const startDate = eventRecord.startDate || date;
    const endDate = eventRecord.endDate || DateHelper.add(startDate, 1, 'day');
    const newRecord = project.extraTaskStore.createRecord();
    const resource = getOriginalRecord(resourceRecord)
    editorHandler.openEditor(EDITOR_KEYS.taskEditor, newRecord, {
      task: newRecord,
      resource,
      removeEventAfterAdd: {eventRecord},
      onAfterRecordAdd: (taskRecord) => {
        eventRecord.remove();
        const [newEvent] = project.eventStore.add({
          startDate,
          endDate,
          taskId: taskRecord.id
        });
        project.assignmentStore.add({
          resource,
          event: newEvent
        });
      }
    });
  }, [editorHandler, project]);

  const addReminderOnTask = ({eventRecord}) => {
    const taskRecord = getOriginalRecord(eventRecord.task);
    entityManager.openEntityManager({ entityType: ENTITY_TYPES.TASK, entityId: taskRecord.id, otherParams: { [WidgetParamKeys.REMINDER.CREATE]: true } });
  };

  const showResourcePopupEditor = useCallback(({ record, column }) => {
    const originalRecord = getOriginalRecord(record);
    if (column.type !== "timeAxis" && canOpenEditor(column, record)) {
      entityManager.openEntityManager({
        entityType: ENTITY_TYPES.RESOURCE,
        entityId: originalRecord.id
      });
    }
  }, [entityManager]);

  const onGridCellClick = debounce(({ column, record, event }) => {
    const originalRecord = getOriginalRecord(record);

    if (canOpenEditor(column, originalRecord) && event.detail === 1 && (column.isTagColumnBase || column.isTeamColumn || column.isAssignToColumn || column.isEligibleUserColumn || column.type === WIDGET_KEYS.columnResourceType)) {
        editorHandler.openEditor(EDITOR_KEYS.entityTagEditor, null, {
          entityRecord: originalRecord,
          event,
          column
        }, { by: "recordType", useExisting: true });
    }
  }, 300)


  const onSwivuBeforePaste = useCallback(({ eventRecords }) => {
    const [eventRecord] = eventRecords;
    if (!eventRecord.taskId) {
      console.warn("Pasted event record does not have task id", eventRecords);
      return false;
    }
  }, []);

  const onSwivuEventPaste = (({ eventRecords, date, source }) => {
    const [eventRecord] = eventRecords;
    const selectedTick = source.timeAxis.getAt(Math.floor(source.timeAxis.getTickFromDate(date)));

    // Align start date to tick while preserving the original event duration
    eventRecord.startDate = DateHelper.clone(selectedTick.startDate);
  })

  return (
    <>
      {bulkCopyRecords && (
        <CopyToBoardDialog
          open={!!bulkCopyRecords}
          onOpenChange={(isOpen) => { if (!isOpen) setBulkCopyRecords(null); }}
          records={bulkCopyRecords}
          entityType={ENTITY_TYPES.RESOURCE}
        />
      )}
      <BryntumSchedulerPro
        ref={(schRef) => {
          ref(schRef);
          schedulerRef.current = schRef;
        }}
        {...props}
        {...SCHEDULER_CONFIG}
        onMouseOver={() => {
          // See SwivuHeaderBottomTier.jsx tickSize change handler for more info
          currentViewRef.current.swivuLastViewportCenterDate = schedulerRef.current.instance.viewportCenterDate;
        }}
        disabled={user && !user.can(GRANTS.grant_four)}
        readOnly={user && !user.can(GRANTS.grant_four)}
        onScheduleDblClick={() => false} // Bryntum v7: Prevent double-click event creation
        onEventDblClick={({eventRecord}) => {
          const taskRecord = getOriginalRecord(eventRecord.task);
          entityManager.openEntityManager({ entityType: ENTITY_TYPES.TASK, entityId: taskRecord.id });
        }}
        onCellClick={onGridCellClick}
        onCellDblClick={showResourcePopupEditor}
        onEventMenuItem={(event) => {
          if (event.item.ref === SwivuSchedulerEventMenuPlugin.itemsKeyRef.newTask) {
            if(event.item.up("menuitem").ref == SwivuSchedulerMenuPlugin.itemsKeyRef.insertTask){
              onCreateNewTask(event);
            }else {
              onCreateNewTaskReplaceEvent(event);
            }
          } else if (event.item.ref === SwivuSchedulerEventMenuPlugin.itemsKeyRef.addReminderOnEvent) {
            addReminderOnTask(event);
          } else if (event.item.ref === SwivuSchedulerEventMenuPlugin.itemsKeyRef.findTask) {
            onTaskFind(event);
          } else if (event.item.ref === SwivuSchedulerEventMenuPlugin.itemsKeyRef.taskFiles) {
            const taskRecord = getOriginalRecord(event.eventRecord.task);
            editorHandler.openEditor(EDITOR_KEYS.taskFilesEditor, taskRecord, {
              entity: taskRecord,
              entityType: "task",
            });
          } else if (event.item.ref === "adjustPoints") {
            openAdjustPointsEditorWithGuard({ project, editorHandler, record: event.eventRecord.task });
          }
        }}
        onScheduleMenuItem={(event) => {
          if (event.item.ref === SwivuSchedulerMenuPlugin.itemsKeyRef.newTask) onCreateNewTask(event);
          else if (event.item.ref === SwivuSchedulerMenuPlugin.itemsKeyRef.findTask) onTaskFind(event);
        }}
        onBeforeDragCreateFinalize={onCreateNewTaskEventByDragCreate}
        stateId={props.stateId}
        plugins={props.plugins}
        onHeaderMenuItem={({source, item}) => {
          if (item._menuId === "resource") {
            const newRecord = source.store.createRecord();
            editorHandler.openEditor(EDITOR_KEYS.resourceEditorV2, newRecord, {resource: newRecord});
          }
        }}
        onPaste={onSwivuEventPaste}
        onBeforePaste={onSwivuBeforePaste}
      />
    </>
  );
});

export default BryntumSchedulerWrapper;
